import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADVISOR_NAV } from "@/pages/advisor/AdvisorHome";
import { ReportThread } from "@/components/dashboard/ReportThread";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SKILLS } from "@/lib/framework";
import { Loader2, Send, ClipboardList, Star } from "lucide-react";

const RATING = { 1: "Emerging", 2: "Developing", 3: "N/A or Unknown", 4: "Proficient", 5: "Advanced" };
const skillName = (id) => (SKILLS.find((s) => s.id === id) || {}).name || id;

function StarRating({ value, onChange, testId }) {
  return (
    <div className="ml-auto flex items-center gap-2" data-testid={testId}>
      <div className="flex">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} title={RATING[n]} data-testid={`${testId}-star-${n}`} className="p-0.5">
            <Star className={`h-4 w-4 transition-colors ${n <= value ? "fill-[#f5b301] text-[#f5b301]" : "text-slate-300 hover:text-[#f5b301]/50"}`} />
          </button>
        ))}
      </div>
      <span className="w-28 text-[11px] font-semibold text-slate-500">{RATING[value]}</span>
    </div>
  );
}

function SkillBadges({ report }) {
  const items = report.skills?.length
    ? report.skills
    : [...(report.strengths || []).map((id) => ({ id, kind: "strength" })), ...(report.focus || []).map((id) => ({ id, kind: "focus" }))];
  if (!items.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {items.map((s, i) => (
        <span key={i} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.kind === "focus" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
          {skillName(s.id)}{s.rating ? ` · ${RATING[s.rating]}` : ""}
        </span>
      ))}
    </div>
  );
}

export default function AdvisorSkillsUpdate() {
  const [students, setStudents] = useState(null);
  const [studentId, setStudentId] = useState("");
  const [skillState, setSkillState] = useState({});
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [reports, setReports] = useState([]);

  useEffect(() => { api.get("/advisor/students").then(({ data }) => setStudents(data)); }, []);

  const loadReports = (sid) => {
    if (!sid) { setReports([]); return; }
    api.get("/advisor/reports", { params: { student_id: sid } }).then(({ data }) => setReports(data)).catch(() => setReports([]));
  };
  useEffect(() => { loadReports(studentId); }, [studentId]);

  const setKind = (id, kind) => setSkillState((st) => {
    const cur = st[id];
    if (cur?.kind === kind) { const n = { ...st }; delete n[id]; return n; }
    return { ...st, [id]: { kind, rating: cur?.rating || 3 } };
  });
  const setRating = (id, rating) => setSkillState((st) => ({ ...st, [id]: { ...(st[id] || { kind: "strength" }), rating: Number(rating) } }));

  const submit = async () => {
    if (!studentId) { toast.error("Select a student first."); return; }
    if (!comment.trim()) { toast.error("Add a comment for the student."); return; }
    setSaving(true);
    try {
      const skills = Object.entries(skillState).map(([id, v]) => ({ id, kind: v.kind, rating: v.rating }));
      await api.post("/advisor/reports", { student_id: studentId, comment: comment.trim(), skills });
      toast.success("Skills update submitted — the student can now see it.");
      setComment(""); setSkillState({}); loadReports(studentId);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <DashboardShell title="Skills Update" subtitle="Submit a transferable-skills update for a student" nav={ADVISOR_NAV}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6 card-elevate">
          <Label className="text-sm font-bold text-[#003057]">Student</Label>
          <Select value={studentId} onValueChange={setStudentId}>
            <SelectTrigger data-testid="su-student" className="mt-1"><SelectValue placeholder={students ? "Select a student" : "Loading…"} /></SelectTrigger>
            <SelectContent>{(students || []).map((s) => <SelectItem key={s.id} value={s.id} data-testid={`su-student-${s.id}`}>{s.name} · {s.program}</SelectItem>)}</SelectContent>
          </Select>

          <p className="mt-5 text-sm font-bold text-[#003057]">Rate skills</p>
          <p className="text-xs text-slate-500">Tag each skill as a Strength or a Focus area, then set the level with the 5-star scale.</p>
          <div className="mt-3 space-y-1.5">
            {SKILLS.map((s) => {
              const sel = skillState[s.id];
              return (
                <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 px-2.5 py-1.5" data-testid={`su-skill-row-${s.id}`}>
                  <span className="w-36 flex-shrink-0 truncate text-sm font-semibold text-slate-700">{s.name}</span>
                  <button type="button" data-testid={`su-strength-${s.id}`} onClick={() => setKind(s.id, "strength")}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${sel?.kind === "strength" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-400 hover:border-emerald-300"}`}>Strength</button>
                  <button type="button" data-testid={`su-focus-${s.id}`} onClick={() => setKind(s.id, "focus")}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${sel?.kind === "focus" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-400 hover:border-amber-300"}`}>Focus</button>
                  {sel && (
                    <StarRating value={sel.rating} onChange={(n) => setRating(s.id, n)} testId={`su-rating-${s.id}`} />
                  )}
                </div>
              );
            })}
          </div>

          <Label className="mt-5 block text-sm font-bold text-[#003057]">Comment to the student</Label>
          <Textarea data-testid="su-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={4} className="mt-1" placeholder="Share observations, encouragement, and next steps…" />
          <Button data-testid="su-submit" onClick={submit} disabled={saving} className="mt-4 w-full rounded-full bg-[#78be21] py-6 font-bold text-[#00243f] hover:bg-[#6aac1c]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Submit skills update</>}
          </Button>
        </Card>

        <Card className="p-6 card-elevate">
          <p className="flex items-center gap-2 font-heading text-base font-bold text-[#003057]"><ClipboardList className="h-5 w-5 text-[#005291]" /> Previous updates {studentId ? "" : "(select a student)"}</p>
          <div className="mt-3 space-y-3">
            {studentId && reports.length === 0 && <p className="text-sm text-slate-400">No updates submitted yet.</p>}
            {reports.map((r) => (
              <div key={r.id} className="rounded-xl border border-slate-200 p-3" data-testid="su-report">
                <div className="flex items-center justify-between"><p className="text-sm font-semibold text-[#003057]">{r.advisor_name}</p><span className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}</span></div>
                <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{r.comment}</p>
                <SkillBadges report={r} />
                <ReportThread report={r} onPosted={() => loadReports(studentId)} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </DashboardShell>
  );
}
